/**
 * Product card option component
 * Compact option selector for product cards (e.g., carat weight circles)
 *
 * @module product-card-option
 * @version 1.0.0
 * @extends HTMLElement
 */
class ProductCardOption extends HTMLElement {

  static CARAT_WEIGHT_ORDER = ['Small', 'Medium', 'Large', 'Extra Large', 'Extra-Large'];

  /** Zodiac signs are shown in calendar order, not alphabetically. */
  static ZODIAC_ORDER = [
    'aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo',
    'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces'
  ];

  /**
   * Centre stone shapes follow the same order as the collection filter tiles
   * (snippets/collection-filters-styles.liquid), so a card, a filter and the PDP
   * all list shapes the same way. Anything not listed here falls in after these,
   * alphabetically.
   */
  static SHAPE_ORDER = [
    'round', 'princess', 'cushion', 'oval', 'pear',
    'heart', 'emerald', 'marquise', 'radiant', 'asscher'
  ];

  /**
   * Metaobject a swatch option takes its icons from, when it differs from the
   * option handle. Drives the `.option-image-small-<metaobject>-<value>` class
   * emitted by the `product-option-setup` snippet.
   */
  static SWATCH_METAOBJECT_BY_HANDLE = {
    'product-center-stone-shape': 'center-stone-shape'
  };

  /** Default number of values shown before collapsing into a "+N" counter. */
  static DEFAULT_MAX_VISIBLE = 4;

  /** All 12 signs are shown (they wrap onto a second row) — a "+8" counter would be useless here. */
  static MAX_VISIBLE_BY_HANDLE = { zodiac: 12, color: 3, 'product-center-stone-shape': 6 };

  /** Options rendered as icon swatches instead of text labels. */
  static SWATCH_HANDLES = ['zodiac', 'product-center-stone-shape'];

  /**
   * Options rendered as chips: a colour dot plus the value's name.
   * Used where the value is a colour, which has no metaobject icon to reuse.
   */
  static CHIP_HANDLES = ['color'];


  static get observedAttributes() {
    return ['active-option', 'tags'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if ((name === 'active-option' || name === 'tags') && oldValue && newValue && oldValue !== newValue && this.hasRendered) {
      if (!this._pendingRender) {
        this._pendingRender = true;
        queueMicrotask(() => {
          this._pendingRender = false;
          this.loadOptions();
        });
      }
    }
  }

  get handle() {
    return this.getAttribute('handle');
  }

  get handleTag() {
    return this.handle + ':';
  }

  get tags() {
    return this.getAttribute('tags').split(' | ').filter(tag => (
      !tag.includes(': null') && !tag.includes(this.handleTag)
    ));
  }

  get activeOption() {
    return this.getAttribute('active-option')
      ? this.handleize(this.getAttribute('active-option'))
      : null;
  }

  get groupTag() {
    return this.getAttribute('group-tag');
  }

  get shortLabel() {
    return this.getAttribute('short-label') || '';
  }

  /**
   * Accessible name for the row. Falls back to the visible label; set it on rows
   * that show icons only (centre stone shape) so the group is still announced.
   */
  get groupLabel() {
    return this.getAttribute('group-label') || this.shortLabel;
  }

  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.groupTag) return;
    this.loadOptions();
  }

  handleize(value) {
    return value
      .toLowerCase()
      .replaceAll('  ', ' ')
      .replaceAll('&', '')
      .replaceAll(' ', '-')
      .replaceAll('--', '-');
  }

  loadOptions() {
    theme.product.getProductHandlesByTag(this.tags).then(products => {
      this.renderOptions(products);
      this.initEvents();
      this.hasRendered = true;
    });
  }

  renderOptions(products) {
    const addedOptions = new Set();
    const optionsData = [];

    products.forEach(product => {
      if (!product.tags.includes(this.groupTag)) return;

      const optionTag = product.tags.filter(tag => tag.includes(this.handleTag));
      if (optionTag.length <= 0) return;

      let label = optionTag[0].split(': ')[1];
      const originalLabel = label;
      const optionHandle = this.handleize(label);

      if (addedOptions.has(optionHandle)) return;

      let numericValue = null;
      let isStringValue = false;

      if (this.handle === 'carat-weight') {
        const parsed = parseFloat(originalLabel);
        if (!isNaN(parsed) && isFinite(parsed)) {
          numericValue = parsed;
          label = parsed.toFixed(2);
        } else {
          isStringValue = true;
          numericValue = Infinity;
        }
      } else if (this.handle === 'width') {
        const match = originalLabel.match(/(\d+(?:\.\d+)?)/);
        numericValue = match ? parseFloat(match[1]) : null;
      }

      optionsData.push({
        productHandle: product.handle,
        optionHandle,
        label,
        numericValue,
        isStringValue
      });

      addedOptions.add(optionHandle);
    });

    if (this.handle === 'zodiac') {
      optionsData.sort((a, b) => {
        const orderA = ProductCardOption.ZODIAC_ORDER.indexOf(a.optionHandle);
        const orderB = ProductCardOption.ZODIAC_ORDER.indexOf(b.optionHandle);
        if (orderA !== -1 && orderB !== -1) return orderA - orderB;
        if (orderA !== -1) return -1;
        if (orderB !== -1) return 1;
        return a.label.localeCompare(b.label);
      });
    }

    if (this.handle === 'product-center-stone-shape') {
      optionsData.sort((a, b) => {
        const orderA = ProductCardOption.SHAPE_ORDER.indexOf(a.optionHandle);
        const orderB = ProductCardOption.SHAPE_ORDER.indexOf(b.optionHandle);
        if (orderA !== -1 && orderB !== -1) return orderA - orderB;
        if (orderA !== -1) return -1;
        if (orderB !== -1) return 1;
        return a.label.localeCompare(b.label);
      });
    }

    if (this.handle === 'color') {
      // Colours have no natural order; sort alphabetically so cards stay stable between renders.
      optionsData.sort((a, b) => a.label.localeCompare(b.label));
    }

    if (this.handle === 'carat-weight' || this.handle === 'width') {
      optionsData.sort((a, b) => {
        if (!a.isStringValue && !b.isStringValue) return a.numericValue - b.numericValue;
        if (a.isStringValue && !b.isStringValue) return 1;
        if (!a.isStringValue && b.isStringValue) return -1;
        const orderA = ProductCardOption.CARAT_WEIGHT_ORDER.indexOf(a.label);
        const orderB = ProductCardOption.CARAT_WEIGHT_ORDER.indexOf(b.label);
        if (orderA !== -1 && orderB !== -1) return orderA - orderB;
        if (orderA !== -1) return -1;
        if (orderB !== -1) return 1;
        return a.label.localeCompare(b.label);
      });
    }

    if (optionsData.length <= 1) return;

    const maxVisible = ProductCardOption.MAX_VISIBLE_BY_HANDLE[this.handle]
      || ProductCardOption.DEFAULT_MAX_VISIBLE;
    const activeIndex = optionsData.findIndex(o => this.activeOption === o.optionHandle);
    const visibleOptions = [];

    optionsData.forEach((option, index) => {
      if (index < maxVisible || index === activeIndex) {
        visibleOptions.push(option);
      }
    });

    const remainingCount = optionsData.length - visibleOptions.length;

    const isChip = ProductCardOption.CHIP_HANDLES.includes(this.handle);

    const valuesHtml = visibleOptions.map(option => (
      isChip ? this.getChipHtml(option) : this.getCircleHtml(option)
    )).join('') + (remainingCount > 0 ? `<span class="product-card-option__remaining">${remainingCount}</span>` : '');

    // Chips name the value themselves, so a row label would only cost card width;
    // it becomes the group's accessible name instead.
    const labelHtml = this.shortLabel && !isChip
      ? `<span class="product-card-option__label">${this.shortLabel}</span>`
      : '';

    const groupAttrs = this.groupLabel ? ` role="group" aria-label="${this.groupLabel}"` : '';

    const isSwatch = ProductCardOption.SWATCH_HANDLES.includes(this.handle);
    const modifier = isSwatch ? ' product-card-option--swatch' : (isChip ? ' product-card-option--chips' : '');
    const valuesModifier = isSwatch ? ' product-card-option__values--swatch' : (isChip ? ' product-card-option__values--chips' : '');

    this.innerHTML = `
      <div class="product-card-option${modifier}">
        ${labelHtml}
        <div class="product-card-option__values${valuesModifier}"${groupAttrs}>${valuesHtml}</div>
      </div>
    `;
  }

  getCircleHtml(option) {
    const isActive = this.activeOption === option.optionHandle;

    // Swatch options reuse the `.option-image-small-<metaobject>-<value>` background-image
    // rules emitted by the `product-option-setup` snippet (same icons as the PDP selector).
    const metaobject = ProductCardOption.SWATCH_METAOBJECT_BY_HANDLE[this.handle] || this.handle;
    const swatchClass = ProductCardOption.SWATCH_HANDLES.includes(this.handle)
      ? ` product-card-option__circle--swatch option-image-small-${metaobject}-${option.optionHandle}`
      : '';

    return `<a href="/products/${option.productHandle}"
      data-handle="${option.productHandle}"
      class="product-card-option__circle${swatchClass}${isActive ? ' active' : ''}"
      aria-label="${option.label}"
      title="${option.label}">${option.label}</a>`;
  }

  /**
   * Colour dot + name. The dot prefers the `color` metaobject image (same source as
   * the metal/gemstone/zodiac swatches), falls back to the shared hex palette in
   * `color-swatches.js`, and lands on a neutral dot for a colour we know nothing
   * about — so a new colour never breaks the card.
   */
  getChipHtml(option) {
    const isActive = this.activeOption === option.optionHandle;

    const image = window.theme?.getColorSwatchImage?.(option.optionHandle);
    const background = image
      ? `url(${image})`
      : window.theme?.getColorSwatchBackground?.(option.optionHandle);

    const dotClass = image
      ? ' product-card-option__dot--image'
      : (background ? '' : ' product-card-option__dot--unknown');
    const dotStyle = background ? ` style="background: ${background}"` : '';

    return `<a href="/products/${option.productHandle}"
      data-handle="${option.productHandle}"
      class="product-card-option__chip${isActive ? ' active' : ''}"
      aria-label="${option.label}"
      title="${option.label}"
      ${isActive ? 'aria-current="true"' : ''}>
      <span class="product-card-option__dot${dotClass}"${dotStyle}></span>
      <span class="product-card-option__chip-label">${option.label}</span>
    </a>`;
  }

  initEvents() {
    this.querySelectorAll('.product-card-option__circle, .product-card-option__chip').forEach(value => {
      value.addEventListener('click', this.handleSelection.bind(this));
    });
  }

  handleSelection(event) {
    event.preventDefault();

    // currentTarget, not target: a chip wraps a dot and a label.
    const selected = event.currentTarget;

    this.querySelectorAll('.product-card-option__circle.active, .product-card-option__chip.active').forEach(el => {
      el.classList.remove('active');
      el.removeAttribute('aria-current');
    });
    selected.classList.add('active');

    this.dispatchEvent(new CustomEvent('change', {
      detail: { handle: selected.dataset.handle },
      bubbles: true,
      composed: true
    }));
  }
}

if (!customElements.get('product-card-option')) {
  customElements.define('product-card-option', ProductCardOption);
}
